// HashtagHashtagType.jsx
import React from 'react';
import { useLoaderData } from 'react-router';

import { createInitialStoreWithRequest } from '../createInitialStore.js';
import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { EMPTY_ARRAY } from '../resource/defaults.js';
import MetaTagsHelmet from '../component/MetaTagsHelmet.jsx';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import useSSRDataMerge from '../hook/useSSRDataMerge.js';
import {
  SUPPORTED_LANGUAGES,
  HASHTAG_FEED_POST_VIDEO,
  HASHTAG_FEED_STORY,
} from '../RemoteConfigKeys.js';
import { prepareCollectionPageMeta } from '../resource/prepareCollectionPageMeta.js';
import UniversalContainer from '../component/UniversalContainer.jsx';
import { getI18nInstance } from '../resource/i18n.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import fetchHashtagSearch from '../action/fetchHashtagSearch.js';
import getFeedName from '../selector/getFeedName.js';
import { fetchFeedTotalCount } from '../resource/fetchFeedTotalCount.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

const ListType = {
  FLIX: 'flix',
  STORY: 'story',
};

const REMOTE_CONFIG_KEY_MAP = {
  [ListType.FLIX]: HASHTAG_FEED_POST_VIDEO,
  [ListType.STORY]: HASHTAG_FEED_STORY,
};

export const loader = async ({ context, params, request }) => {
  const log = legacySSRLog.extend('HashtagHashtagType loader()');
  const errorLog = legacySSRError.extend('HashtagHashtagType loader()');
  log('start', { context, params, request });

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    const { store } = await createInitialStoreWithRequest({
      params,
      request,
    });

    log('createInitialStoreWithRequest', { store: store.getState().toJS() });

    // Get hashtag feed from remote config based on type
    let state = store.getState();
    const { hashtag, type } = params;

    // Validate type
    const validTypes = ['flix', 'story'];
    if (!validTypes.includes(type)) {
      // Invalid type, return noindex
      return {
        data: { store: {} },
        params,
        meta: [{ name: 'robots', content: 'noindex, nofollow' }],
        staticContext: {
          requestHeaders: {
            hostname: request?.headers?.get('host') || 'swag.live',
          },
        },
      };
    }

    const feedNameRemoteConfigKey = REMOTE_CONFIG_KEY_MAP[type];
    const hashtagFeedName = getFeedName(
      state,
      feedNameRemoteConfigKey,
      '{HASHTAG}',
      hashtag
    );

    // Fetch hashtag search and feed count in parallel
    const [, feedCountResult] = await Promise.allSettled([
      store
        .dispatch(fetchHashtagSearch({ query: `#${hashtag}` }))
        .catch(() => undefined),
      hashtagFeedName
        ? fetchFeedTotalCount({
            feedNameWithQuery: hashtagFeedName,
            httpProxyHeaders,
          })
        : Promise.resolve(0),
    ]);

    const feedCount =
      feedCountResult.status === 'fulfilled' ? feedCountResult.value : 0;

    state = store.getState();
    const storeData = state.toJS();
    log('fetched data', { store: storeData, feedCount });

    // Get i18n instance
    const { i18n } = getI18nInstance();

    // Get hashtag display name from translation
    const hashtagI18nKey = `hashtag_${hashtag}`;
    const hashtagName = i18n.customExists(hashtagI18nKey, {
      ns: TranslationNamespace.SEARCH,
    })
      ? i18n.t(hashtagI18nKey, { ns: TranslationNamespace.SEARCH })
      : hashtag;

    // Get type display name
    const typeDisplayName = type === 'flix' ? 'Video' : 'Story';

    // Prepare title and description
    const title = i18n.t(`seo_hashtag_${type}_title`, {
      ns: TranslationNamespace.SEO,
      hashtag: hashtagName,
    });
    const description =
      i18n.t(`seo_hashtag_${hashtag}_${type}_description`, {
        ns: TranslationNamespace.SEO,
      }) ||
      i18n.t(`seo_hashtag_${type}_description`, {
        ns: TranslationNamespace.SEO,
        hashtag: hashtagName,
      });

    const availableLanguages = getRemoteConfigData(state, SUPPORTED_LANGUAGES);
    const canonicalPath = `/hashtag/${hashtag}/${type}`;
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareCollectionPageMeta({
      state,
      title,
      description,
      keywords: [hashtagName, typeDisplayName, 'SWAG', 'hashtag'],
      hostname: request?.headers?.get('host') || 'swag.live',
      canonical,
      alternates,
      includeJsonLd: true,
      schemaType: 'VideoGallery', // Use VideoGallery for hashtag pages (matching Next.js)
    });

    log('meta data prepared', { meta });

    return {
      data: { store: {} },
      params,
      meta,
      staticContext: {
        requestHeaders: {
          hostname: request?.headers?.get('host') || 'swag.live',
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });
    return { data: null, error, params };
  }
};

const HashtagHashtagTypeRoute = () => {
  const { data, error, meta = EMPTY_ARRAY } = useLoaderData();

  useSSRDataMerge({
    componentName: 'HashtagHashtagType',
    loaderData: data,
    loaderError: error,
  });

  return (
    <>
      <MetaTagsHelmet metaTags={meta} />
      <UniversalContainer filename="Hashtag" />
    </>
  );
};

export default HashtagHashtagTypeRoute;
